import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUp } from "lucide-react";
import { useScheduleCall } from "../context/ScheduleCallContext";
const LOGO_SRC = "/photos/purposeLogo.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();
  const { openScheduleCall } = useScheduleCall();

  const services = [
    "Lead Generation Websites",
    "Booking Platforms",
    "AI Call Agents",
    "Custom CRM Systems",
    "Business Automation",
    "Full-Stack Apps",
  ];

  const goTo = (hash) => {
    if (location.pathname === "/") {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/${hash}`);
    }
  };

  return (
    <footer className="bg-dark-500 border-t border-white/5" aria-label="Site footer">
      <div className="section-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_SRC} alt="Purpose Labs logo" className="h-14 w-auto" loading="lazy" />
              <span className="font-display text-xl font-bold text-white">
                Purpose Labs
              </span>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed max-w-md mb-6">
              IT and research services — lead gen websites, booking platforms,
              CRMs, AI call agents, and free online tools.
            </p>
            <div className="space-y-1.5 text-sm text-gray-500">
              <p>
                <a href="mailto:master@purposelabs.in" className="hover:text-primary-400 transition-colors">
                  master@purposelabs.in
                </a>
              </p>
              <p>
                <a href="tel:+918178462090" className="hover:text-primary-400 transition-colors">
                  +91 8178462090
                </a>
              </p>
              <p className="text-xs text-gray-600 pt-2">
                GST: 09IQTPS2248H1ZZ
              </p>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-display font-semibold text-white mb-4 text-sm">
              Services
            </h4>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service}>
                  <button
                    onClick={() => goTo("#services")}
                    className="text-sm text-gray-500 hover:text-primary-400 transition-colors text-left"
                  >
                    {service}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-display font-semibold text-white mb-4 text-sm">
              Quick Links
            </h4>
            <ul className="space-y-2">
              {[
                { label: "SaaS Products", href: "#products" },
                { label: "Our Work", href: "#portfolio" },
                { label: "Schedule a Call", onClick: openScheduleCall },
                { label: "Free Online Tools", href: "/tools", isRoute: true },
                { label: "About Us", href: "#about" },
                { label: "Contact", href: "#contact" },
              ].map((link) => (
                <li key={link.label}>
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-sm text-gray-500 hover:text-primary-400 transition-colors"
                    >
                      {link.label}
                    </Link>
                  ) : link.onClick ? (
                    <button
                      onClick={link.onClick}
                      className="text-sm text-gray-500 hover:text-primary-400 transition-colors"
                    >
                      {link.label}
                    </button>
                  ) : (
                    <button
                      onClick={() => goTo(link.href)}
                      className="text-sm text-gray-500 hover:text-primary-400 transition-colors"
                    >
                      {link.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-600 text-xs">
            © {currentYear} Purpose Labs. All rights reserved.
          </p>
          <p className="text-gray-600 text-xs">
            Built with purpose. Designed to convert.
          </p>
        </div>
      </div>

      {/* Scroll to top */}
      <motion.button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="fixed bottom-6 right-6 w-11 h-11 bg-primary-600 text-white rounded-xl shadow-glow hover:bg-primary-500 transition-colors z-40 flex items-center justify-center"
        whileHover={{ scale: 1.08, y: -2 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Scroll to top"
      >
        <ArrowUp className="w-5 h-5" strokeWidth={1.75} />
      </motion.button>
    </footer>
  );
};

export default Footer;
